import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { to: "/", label: "Index", testid: "nav-link-home" },
  { to: "/gallery", label: "The Work", testid: "nav-link-gallery" },
  { to: "/book", label: "Book a Session", testid: "nav-link-book" },
  { to: "/client-access", label: "Client Access", testid: "nav-link-client-access" },
];

const galleryLinks = [
  { to: "/gallery?cat=automotive", label: "Automotive", testid: "mobile-nav-automotive" },
  { to: "/gallery?cat=personal", label: "Personal / Portrait", testid: "mobile-nav-personal" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { pathname, search } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname, search]);

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header className="md:sticky md:top-0 inset-x-0 z-40 border-b border-white/10 bg-ink pt-[env(safe-area-inset-top)]">
        <div className="px-4 sm:px-8 lg:px-16 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="group flex items-baseline gap-3 min-w-0">
            <span className="font-serif text-2xl tracking-tight text-bone whitespace-nowrap transition-[letter-spacing] duration-500 group-hover:tracking-normal">
              Maddie <em className="text-flame">Reiss</em>
            </span>
            <span className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.3em] text-faint">
              Photography
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `font-mono text-xs uppercase tracking-[0.25em] transition-colors duration-300 ${
                    isActive ? "text-flame" : "text-ash hover:text-bone"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <button
            className="md:hidden text-bone p-2 -mr-2"
            data-testid="nav-menu-button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 bg-ink flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="h-16 px-4 flex items-center justify-between border-b border-white/10 shrink-0">
              <span className="font-serif text-2xl text-bone">
                Maddie <em className="text-flame">Reiss</em>
              </span>
              <button
                data-testid="nav-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="p-2 -mr-2"
              >
                <X size={24} className="text-bone" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-6 gap-7 overflow-y-auto">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.08 + i * 0.08, duration: 0.5 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    onClick={() => setOpen(false)}
                    className="font-serif text-5xl text-bone flex items-center gap-4"
                  >
                    {l.label}
                    <ArrowUpRight className="text-flame" size={28} />
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.35, duration: 0.5 }}
                className="pt-6 mt-2 border-t border-white/10 flex gap-6"
              >
                {galleryLinks.map((g) => (
                  <Link
                    key={g.to}
                    to={g.to}
                    data-testid={g.testid}
                    onClick={() => setOpen(false)}
                    className="font-mono text-xs uppercase tracking-[0.25em] text-ash hover:text-flame transition-colors"
                  >
                    {g.label}
                  </Link>
                ))}
              </motion.div>
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.45, duration: 0.5 }}
              className="px-6 pb-8 shrink-0"
            >
              <a
                href="mailto:Maddietweetrbg@gmail.com"
                data-testid="mobile-nav-email"
                className="block font-mono text-[11px] tracking-[0.1em] text-faint break-all"
              >
                Maddietweetrbg@gmail.com
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
